import React from 'react';
import styled, { keyframes } from 'styled-components';
import { getColorScheme } from '../theme/colorSchemes';
import { t as tOinp } from '../assessments/oinp/i18n';

const LABELS = {
  en: { crs: 'Free CRS / IRCC Score Check', intake: 'Continue to Consultation', or: 'or' },
  fr: { crs: 'Évaluation CRS / IRCC Gratuite', intake: 'Continuer la Consultation', or: 'ou' },
  es: { crs: 'Evaluación CRS / IRCC Gratis', intake: 'Continuar a Consulta', or: 'o' },
  pt: { crs: 'Avaliação CRS / IRCC Grátis', intake: 'Continuar para Consulta', or: 'ou' },
  he: { crs: 'בדיקת CRS / IRCC חינמית', intake: 'המשך לייעוץ', or: 'או' },
  ar: { crs: 'فحص CRS / IRCC مجاني', intake: 'متابعة الاستشارة', or: 'أو' },
  zh: { crs: '免费 CRS / IRCC 评分检查', intake: '继续咨询', or: '或' },
  ru: { crs: 'Бесплатная проверка CRS / IRCC', intake: 'Продолжить консультацию', or: 'или' },
  hi: { crs: 'मुफ्त CRS / IRCC स्कोर जांच', intake: 'परामर्श जारी रखें', or: 'या' },
  ko: { crs: '무료 CRS / IRCC 점수 확인', intake: '상담 계속하기', or: '또는' },
  tl: { crs: 'Libreng CRS / IRCC Score Check', intake: 'Magpatuloy sa Konsultasyon', or: 'o' },
  fa: { crs: 'بررسی رایگان امتیاز CRS / IRCC', intake: 'ادامه مشاوره', or: 'یا' },
  tr: { crs: 'Ücretsiz CRS / IRCC Puan Kontrolü', intake: 'Danışmanlığa Devam Et', or: 'veya' },
  uk: { crs: 'Безкоштовна перевірка CRS / IRCC', intake: 'Продовжити консультацію', or: 'або' },
  vi: { crs: 'Kiểm tra điểm CRS / IRCC miễn phí', intake: 'Tiếp tục tư vấn', or: 'hoặc' },
  ja: { crs: '無料 CRS / IRCC スコアチェック', intake: '相談を続ける', or: 'または' },
  ur: { crs: 'مفت CRS / IRCC سکور چیک', intake: 'مشاورت جاری رکھیں', or: 'یا' },
  it: { crs: 'Controllo CRS / IRCC Gratuito', intake: 'Continua la Consulenza', or: 'o' },
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  display: flex; flex-direction: column; gap: 10px;
  padding: 4px 0; animation: ${fadeIn} 0.4s ease-out;
  width: 100%; max-width: 320px;
`;

const ChoiceBtn = styled.button`
  display: flex; align-items: center; gap: 10px;
  width: 100%; padding: 13px 16px; border-radius: 12px;
  border: 1.5px solid ${p => {
    const c = getColorScheme(p.$scheme);
    return p.$primary ? c.primary : c.accentBorder;
  }};
  background: ${p => {
    const c = getColorScheme(p.$scheme);
    return p.$primary ? `linear-gradient(135deg, ${c.primary}, ${c.primaryLight})` : '#ffffff';
  }};
  color: ${p => p.$primary ? '#ffffff' : '#1a2332'};
  cursor: pointer;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 13.5px; font-weight: 600;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: left;
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${p => {
      const c = getColorScheme(p.$scheme);
      return p.$primary ? `0 6px 20px ${c.accentShadow}` : '0 6px 20px rgba(0,0,0,0.08)';
    }};
  }
  &:active { transform: translateY(0); }
`;

const BtnIcon = styled.span`
  font-size: 18px; line-height: 1; flex-shrink: 0;
`;

const Divider = styled.div`
  display: flex; align-items: center; gap: 10px;
  font-size: 11px; color: #94a3b8;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  &::before, &::after {
    content: ''; flex: 1; height: 1px; background: #e8ecf1;
  }
`;

/**
 * Which assessment buttons exist, keyed by the template id the server sends in
 * `expertData.widgetConfig.assessmentTemplates`. The SERVER decides which ids apply to
 * this adviser's discipline — this map only says how each one looks. Adding a template
 * for a new discipline is one entry here plus its questionnaire phase.
 */
const TEMPLATE_UI = {
  crs: { icon: '🎯', label: (t) => t.crs, primary: true },
  oinp: { icon: '🍁', label: (t, language) => tOinp(language, 'oinpButton') },
  'oinp-employer': { icon: '🏢', label: (t, language) => tOinp(language, 'employerButton') },
};

const InlineChoiceButtons = ({
  /**
   * Assessment template ids to offer, in display order. Comes from the resolved
   * widget config, so a non-immigration adviser passes [] and only the "continue to
   * consultation" action renders (and the caller normally skips this step entirely).
   */
  templates = [],
  onChoose,
  onChooseIntake,
  darkMode = false,
  scheme = 'blue',
  language = 'en',
}) => {
  const t = LABELS[language] || LABELS.en;

  const offered = templates
    .map(id => ({ id, ui: TEMPLATE_UI[id] }))
    // Unknown id = a template the server knows about but this widget build doesn't
    // implement yet. Skip it rather than crash — the deploys are independent.
    .filter(x => x.ui && typeof onChoose === 'function');

  return (
    <Wrapper>
      {offered.map(({ id, ui }, i) => (
        <ChoiceBtn
          key={id}
          $scheme={scheme}
          // The first offered assessment is the visual call-to-action, whatever it is.
          $primary={i === 0}
          onClick={() => onChoose(id)}
        >
          <BtnIcon>{ui.icon}</BtnIcon>
          {ui.label(t, language)}
        </ChoiceBtn>
      ))}
      {offered.length > 0 && <Divider>{t.or}</Divider>}
      <ChoiceBtn $scheme={scheme} $primary={offered.length === 0} onClick={onChooseIntake}>
        <BtnIcon>💬</BtnIcon>
        {t.intake}
      </ChoiceBtn>
    </Wrapper>
  );
};

export default InlineChoiceButtons;
